import React, { useEffect, useState } from 'react';
import { Form, Image, Input, Select } from 'antd';
import { CloseOutlined } from '@ant-design/icons';
import { useAppDispatch } from '../../../../../hooks/redux';

import {
    addLiveGuidelineOwnerAction,
    getLiveGuidelinePotentialOwnersAction,
    removeLiveGuidelineOwnerAction,
    updateLiveGuidelineAction,
} from '../../../../../redux/actions/liveGuidelineActions';
import {
    LiveGuidelineDetail,
    LiveGuidelineOwner,
} from '../../../../../redux/actions/liveGuidelineActions/types';
import { PrimaryButton, Text } from '../../../../../styles/common.styles';
import { alertService } from '../../../../../services';
import { toShortLocalDate } from '../../../../../helpers/dateHelper';
import { blueDark01 } from '../../../../../styles/colors';
import { formatLiveGuidelineCriteria } from '../../../../../helpers/liveGuidelineCriteria';
import { useMasterFormatTree } from '../../../../../hooks/masterFormat';
import {
    GuidelineInformationContainer,
    OwnerTag,
    OwnersList,
    SectionTitle,
    CriteriaGroup,
    BasicInfoDivider,
    CriteriaGroupTitle,
    CriteriaGroupValue,
    CriteriaGroupLabel,
    CriteriaHeader,
    CriteriaSection,
    DescriptionTextArea,
    EditCriteriaButton,
    FieldLabel,
    FieldRow,
    FieldInput,
    GuidelineInformationLayout,
    LastUpdatedText,
    LeftColumn,
    RightColumn,
    SaveButton,
    SaveButtonContainer,
    FilterContainer,
    AddButton,
    SearchCriteriaSectionTitle,
    SearchCriteriaSection,
    BasicInformationSection,
    DescriptionInformationSection,
    OwnersSelect,
} from './GuidelineInformationTab.styles';
import { useModalState } from '../../../../../hooks/modalState';
import DeleteModal from '../../../../../components/common/deleteModal/DeleteModal';
import { ActionResponse } from '../../../../../models/actionResponse/types';
import { openCatalogForLiveGuideline } from '../../../../../helpers/liveGuidelineRedirect';
const { Option } = Select;

const { TextArea } = Input;

interface Props {
    guideline: LiveGuidelineDetail | null;
    reload: () => Promise<void>;
    canManageOwners: boolean;
}

interface FormValues {
    name: string;
    description?: string;
}

const GuidelineInformationTab: React.FC<Props> = ({ guideline, reload, canManageOwners }) => {
    const dispatch = useAppDispatch();
    const deleteModal = useModalState();
    const [form] = Form.useForm<FormValues>();
    const [newOwnerId, setNewOwnerId] = useState<number | null>(null);
    const [availableOwners, setAvailableOwners] = useState<LiveGuidelineOwner[]>([]);
    const [owners, setOwners] = useState<LiveGuidelineOwner[]>(guideline?.owners ?? []);
    const [ownersLoading, setOwnersLoading] = useState(false);
    const [addingOwner, setAddingOwner] = useState(false);
    const [ownerToDelete, setOwnerToDelete] = useState<LiveGuidelineOwner | null>(null);
    const [deletePending, setDeletePending] = useState(false);
    const masterFormatTree = useMasterFormatTree();

    useEffect(() => {
        form.setFieldsValue({
            name: guideline?.name ?? '',
            description: guideline?.description ?? '',
        });
    }, [guideline, form]);

    useEffect(() => {
        setOwners(guideline?.owners ?? []);
    }, [guideline]);

    useEffect(() => {
        let isCurrent = true;
        const loadAvailableOwners = async () => {
            if (!guideline) {
                setAvailableOwners([]);
                setOwnersLoading(false);
                return;
            }

            setOwnersLoading(true);
            try {
                const result = (await dispatch(
                    getLiveGuidelinePotentialOwnersAction(guideline.id),
                )) as ActionResponse<Array<LiveGuidelineOwner>>;

                if (
                    isCurrent &&
                    getLiveGuidelinePotentialOwnersAction.fulfilled.match(result) &&
                    result.payload
                ) {
                    setAvailableOwners(result.payload.data);
                }
            } finally {
                if (isCurrent) {
                    setOwnersLoading(false);
                }
            }
        };

        loadAvailableOwners();
        return () => {
            isCurrent = false;
        };
    }, [dispatch, guideline?.id]);

    const onSave = async (values: FormValues) => {
        if (!guideline || !canManageOwners) {
            return;
        }
        await dispatch(
            updateLiveGuidelineAction({
                id: guideline.id,
                name: values.name,
                description: values.description,
            }),
        );
        alertService.success('Live Guideline updated');
        await reload();
    };

    const onAddOwner = async () => {
        if (!guideline || !newOwnerId || !canManageOwners) {
            return;
        }
        setAddingOwner(true);
        try {
            await dispatch(
                addLiveGuidelineOwnerAction({
                    liveGuidelineId: guideline.id,
                    userId: newOwnerId,
                }),
            ).unwrap();

            const selectedOwner = addableOwners.find((owner) => owner.userId === newOwnerId);

            if (selectedOwner) {
                setOwners((currentOwners) => [...currentOwners, selectedOwner]);
            }

            setNewOwnerId(null);

            await reload();
        } catch (error) {
            console.error('Failed to add owner:', error);
        } finally {
            setAddingOwner(false);
        }
    };

    const onRemoveOwner = (owner: LiveGuidelineOwner) => {
        if (!guideline || !canManageOwners) {
            return;
        }

        setOwnerToDelete(owner);
        deleteModal.onOpen();
    };

    const confirmRemoveOwner = async () => {
        if (!guideline || !ownerToDelete || !canManageOwners) {
            return;
        }

        setDeletePending(true);
        try {
            const action = await dispatch(
                removeLiveGuidelineOwnerAction({
                    liveGuidelineId: guideline.id,
                    userId: ownerToDelete.userId,
                }),
            );
            // The server rejects removing the last owner; refresh only after a successful removal.
            if (removeLiveGuidelineOwnerAction.fulfilled.match(action)) {
                setOwners((currentOwners) =>
                    currentOwners.filter(
                        (currentOwner) => currentOwner.userId !== ownerToDelete.userId,
                    ),
                );
                await reload();
                setOwnerToDelete(null);
                deleteModal.onClose();
            }
        } finally {
            setDeletePending(false);
        }
    };

    const handleCriteria = () => {
        window.location.href = `${process.env.REACT_APP_TC_URL}/search?guideline=${guideline?.id}`;
    };

    const currentOwnerIds = new Set(owners.map((owner) => owner.userId));
    const addableOwners = availableOwners.filter((owner) => !currentOwnerIds.has(owner.userId));
    const criteriaGroups = formatLiveGuidelineCriteria(guideline?.criteria ?? [], masterFormatTree);

    return (
        <GuidelineInformationContainer>
            {canManageOwners && (
                <SaveButtonContainer>
                    <SaveButton onClick={() => form.submit()}>Save</SaveButton>
                </SaveButtonContainer>
            )}

            <Form layout="vertical" form={form} onFinish={onSave}>
                <GuidelineInformationLayout>
                    <LeftColumn>
                        <BasicInformationSection>
                            <SectionTitle>Basic information</SectionTitle>

                            <FieldRow>
                                <FieldLabel>Live Guideline name*</FieldLabel>

                                <FieldInput>
                                    <Form.Item
                                        name="name"
                                        rules={[
                                            {
                                                required: true,
                                                message: 'Name is required',
                                            },
                                        ]}
                                        style={{ margin: 0 }}
                                    >
                                        <Input maxLength={200} disabled={!canManageOwners} />
                                    </Form.Item>
                                </FieldInput>
                            </FieldRow>

                            <FieldRow>
                                <FieldLabel>Creator/owners*</FieldLabel>

                                <OwnersList>
                                    {owners.map((owner) => (
                                        <OwnerTag
                                            key={owner.id}
                                            closable={canManageOwners && owners.length > 1}
                                            closeIcon={
                                                <CloseOutlined
                                                    style={{
                                                        fontSize: 10,
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                    }}
                                                />
                                            }
                                            onClose={(e) => {
                                                e.preventDefault();
                                                onRemoveOwner(owner);
                                            }}
                                        >
                                            {owner.firstName || owner.lastName
                                                ? `${owner.firstName ?? ''} ${
                                                      owner.lastName ?? ''
                                                  }`.trim()
                                                : owner.email ?? String(owner.userId)}
                                        </OwnerTag>
                                    ))}
                                </OwnersList>
                            </FieldRow>
                            {canManageOwners && (
                            <FieldRow>
                                <FilterContainer>
                                    <FieldLabel>Add creator/owner</FieldLabel>

                                    <OwnersSelect
                                        aria-label="Select additional creator or owner"
                                        placeholder="Select additional creator or owner"
                                        loading={ownersLoading}
                                        disabled={ownersLoading || !addableOwners.length}
                                        showSearch
                                        optionFilterProp="children"
                                        filterOption={(input, option) =>
                                            String(option?.children ?? '')
                                                .toLowerCase()
                                                .includes(input.toLowerCase())
                                        }
                                        onChange={(userId: number) => setNewOwnerId(userId)}
                                        value={newOwnerId ?? undefined}
                                        suffixIcon={
                                            <Image
                                                src="src/assets/images/ic-dropdown-arrow.svg"
                                                preview={false}
                                            />
                                        }
                                    >
                                        {addableOwners.map((owner) => {
                                            const name = `${owner.firstName ?? ''} ${
                                                owner.lastName ?? ''
                                            }`.trim();

                                            return (
                                                <Option
                                                    style={{
                                                        fontSize: 12,
                                                    }}
                                                    value={owner.userId}
                                                    key={owner.userId}
                                                >
                                                    {name || owner.email}
                                                </Option>
                                            );
                                        })}
                                    </OwnersSelect>

                                    <AddButton
                                        disabled={!newOwnerId || addingOwner}
                                        onClick={onAddOwner}
                                    >
                                        Add
                                    </AddButton>
                                </FilterContainer>
                            </FieldRow>
                            )}
                        </BasicInformationSection>

                            <SearchCriteriaSection>
                                <SearchCriteriaSectionTitle>Search criteria</SearchCriteriaSectionTitle>

                                <CriteriaHeader>
                                    {canManageOwners && (
                                        <EditCriteriaButton>
                                            <PrimaryButton
                                                height={32}
                                                width={99}
                                                onClick={() =>
                                                    guideline &&
                                                    openCatalogForLiveGuideline(
                                                        guideline.id,
                                                        guideline.criteria,
                                                    )
                                                }
                                            >
                                                <Text weight={400} size={14} height={18}>
                                                    Edit criteria
                                                </Text>
                                            </PrimaryButton>
                                        </EditCriteriaButton>
                                    )}
                                    {guideline?.modifiedOn && (
                                        <LastUpdatedText>
                                            Last updated {toShortLocalDate(guideline.modifiedOn)}
                                            {guideline.modifiedByName
                                                ? ` (${guideline.modifiedByName})`
                                                : ''}
                                        </LastUpdatedText>
                                    )}
                                </CriteriaHeader>
                            </SearchCriteriaSection>

                            <CriteriaSection>
                                {criteriaGroups.length ? (
                                    criteriaGroups.map((group) => (
                                        <CriteriaGroup key={group.title}>
                                            <CriteriaGroupTitle>{group.title}</CriteriaGroupTitle>
                                            {group.entries.map((entry, index) => (
                                                <CriteriaGroupValue
                                                    key={`${entry.label ?? entry.value}-${index}`}
                                                >
                                                    {entry.label && (
                                                        <CriteriaGroupLabel>
                                                            {entry.label}:{' '}
                                                        </CriteriaGroupLabel>
                                                    )}
                                                    {entry.value}
                                                </CriteriaGroupValue>
                                            ))}
                                        </CriteriaGroup>
                                    ))
                                ) : (
                                    <Text weight={400} size={13} height={18} color={blueDark01}>
                                        No criteria set.
                                    </Text>
                                )}
                            </CriteriaSection>
                    </LeftColumn>

                    <RightColumn>
                        <DescriptionInformationSection>
                            <SectionTitle>Description</SectionTitle>
                            <FieldRow>
                                <FieldLabel>Description</FieldLabel>
                                <DescriptionTextArea>
                                    <Form.Item name="description" style={{ margin: 0 }}>
                                        <TextArea rows={6} maxLength={2000} disabled={!canManageOwners} />
                                    </Form.Item>
                                </DescriptionTextArea>
                            </FieldRow>
                        </DescriptionInformationSection>
                    </RightColumn>
                </GuidelineInformationLayout>
            </Form>

            <BasicInfoDivider />
            {canManageOwners && (
                <SaveButtonContainer style={{ marginTop: 40 }}>
                    <SaveButton onClick={() => form.submit()}>Save</SaveButton>
                </SaveButtonContainer>
            )}
            {ownerToDelete && (
                <DeleteModal
                    icon="trash-can-list.svg"
                    isOpen={deleteModal.isOpen}
                    closeModal={() => {
                        setOwnerToDelete(null);
                        deleteModal.onClose();
                    }}
                    reload={confirmRemoveOwner}
                    title="creator/owner"
                    text={`${
                        `${ownerToDelete.firstName ?? ''} ${ownerToDelete.lastName ?? ''}`.trim() ||
                        ownerToDelete.email ||
                        String(ownerToDelete.userId)
                    } as a creator/owner`}
                    pending={deletePending}
                />
            )}
        </GuidelineInformationContainer>
    );
};

export default GuidelineInformationTab;
